import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Grow } from '@material-ui/core';
import {
  isTwilioConnected,
  selectChatInterfaceState,
  selectTotalUnreadCount,
} from 'models/twilio';
import styled from 'styled-components';
import CircularLoadingIndicator from 'components/indicators/CircularLoadingIndicator';
import { useTranslation } from 'react-i18next';
import TRANSLATIONS from 'components/twilio/translations';
import { BEHAVIOR, COLORS } from '../../constants';
import UserNameText from '../styled/UserNameText';
import { ChatCloseButton, ChatFullscreenButton } from '../CommonChatButtons';
import ChatHeaderContainer from '../styled/ChatHeaderContainer';
import ChatBellButton from '../ChatBellButton';
import TwilioChatIcon from '../assets/TwilioChatIcon';

const StyledCircularLoadingIndicator = React.memo(styled(CircularLoadingIndicator)`
  margin: 0 0 0 8px;
  display: flex;
`);

const StyledChatBellButton = React.memo(styled(ChatBellButton)`
  margin-right: 0.25rem;

  &:hover {
    background: rgba(0, 0, 0, 0.2);
  }
`);

const StyledChatCloseButton = React.memo(styled(ChatCloseButton)`
  &:hover {
    background: rgba(0, 0, 0, 0.2);
  }
`);

const StyledChatFullscreenButton = styled(ChatFullscreenButton)`
  &:hover {
    background: rgba(0, 0, 0, 0.2);
  }
`;

const UnreadIndicatorContainer = styled.div`
  background: #CF2F2F;
  height: 20px;
  width: 36px;
  border-radius: 10px;
  margin-left: 11px;

  font-family: Lato;
  font-size: 14px;
  font-weight: bold;
  
  line-height: 20px;
  color: white;
  text-align: center;
`;

const StretchableContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  margin-left: 10px;
`;

const UnreadIndicator: FC = () => {
  const totalUnreadCount = useSelector(selectTotalUnreadCount);

  return (
    <Grow in={Boolean(totalUnreadCount)}>
      <UnreadIndicatorContainer>
        {totalUnreadCount > BEHAVIOR.CHAT_BADGE_MAX ? `${BEHAVIOR.CHAT_BADGE_MAX}+` : totalUnreadCount}
      </UnreadIndicatorContainer>
    </Grow>
  );
};

const ChatHeader: FC = () => {
  const { t } = useTranslation();
  const chatInterfaceActive = useSelector(selectChatInterfaceState);
  const twilioDisconnected = !useSelector(isTwilioConnected);

  const nameComponent = useMemo(() => (
    <StretchableContainer>
      <UserNameText>
        {t(...TRANSLATIONS.COMMON.CHAT)}
      </UserNameText>
      <UnreadIndicator />
    </StretchableContainer>
  ), [t]);

  const buttonComponent = useMemo(() => (
    chatInterfaceActive && (
      <>
        <StyledChatBellButton iconColor={COLORS.WHITE} />
        <StyledChatFullscreenButton iconColor={COLORS.WHITE} />
        <StyledChatCloseButton id="desktop-chat-close-button" iconColor={COLORS.WHITE} />
      </>
    )
  ), [chatInterfaceActive]);

  return (
    <ChatHeaderContainer
      id="desktop-chat-header"
      $chatInterfaceActive={chatInterfaceActive}
      $twilioDisconnected={twilioDisconnected}
    >
      <TwilioChatIcon height={24} width={24} />
      {nameComponent}
      {buttonComponent}
      {twilioDisconnected && <StyledCircularLoadingIndicator size={24} />}
    </ChatHeaderContainer>
  );
};

export default ChatHeader;
